import { trpc } from '@/lib/trpc';
import type { FinopsAlertsFilters, FinopsProvider } from '../../../shared/types';

/**
 * FinOps data changes weekly/monthly — a long staleTime with no focus refetch
 * also caps BigQuery scan spend from page views.
 */
const STALE = 5 * 60 * 1000;
const OPTS = { staleTime: STALE, refetchOnWindowFocus: false } as const;

// ─── Overview (Ramp — "Tech & Product - Tools" card) ─────────────────────────

export const useFinopsOverviewSummary = (month?: string) =>
  trpc.finops.overviewSummary.useQuery({ month }, OPTS);

export const useFinopsOverviewTrend = (months = 12) =>
  trpc.finops.overviewTrend.useQuery({ months }, OPTS);

export const useFinopsOverviewVendors = (month?: string) =>
  trpc.finops.overviewVendors.useQuery({ month }, OPTS);

export const useFinopsOverviewCategories = (month?: string) =>
  trpc.finops.overviewCategories.useQuery({ month }, OPTS);

// ─── AWS (cloud_costs snapshots) ─────────────────────────────────────────────

export const useFinopsAwsMonth = (month?: string) =>
  trpc.finops.awsMonth.useQuery({ month }, OPTS);

/** `enabled` lets a caller defer the prior-week query until the current week's period is known. */
export const useFinopsAwsWeekly = (week?: string, enabled = true) =>
  trpc.finops.awsWeekly.useQuery({ week }, { ...OPTS, enabled });

/** `account` is part of the query key, so switching accounts refetches with the drawer open. */
export const useFinopsAwsResources = (
  week: string,
  service: string,
  account?: string,
  enabled = true,
) => trpc.finops.awsResources.useQuery({ week, service, account }, { ...OPTS, enabled });

export const useFinopsAwsForecast = () =>
  trpc.finops.awsForecast.useQuery(undefined, OPTS);

// ─── GCP (billing export, queried live) ──────────────────────────────────────

export const useFinopsGcpMonth = (month?: string) =>
  trpc.finops.gcpMonth.useQuery({ month }, OPTS);

export const useFinopsGcpWeekly = (week?: string, enabled = true) =>
  trpc.finops.gcpWeekly.useQuery({ week }, { ...OPTS, enabled });

export const useFinopsGcpResources = (
  week: string,
  project?: string,
  service?: string,
  enabled = true,
) => trpc.finops.gcpResources.useQuery({ week, project, service }, { ...OPTS, enabled });

export const useFinopsGcpForecast = () =>
  trpc.finops.gcpForecast.useQuery(undefined, OPTS);

// ─── Vercel (cloud_costs.vercel_daily, collector-written) ────────────────────

export const useFinopsVercelMonth = (month?: string) =>
  trpc.finops.vercelMonth.useQuery({ month }, OPTS);

export const useFinopsVercelWeekly = (week?: string, enabled = true) =>
  trpc.finops.vercelWeekly.useQuery({ week }, { ...OPTS, enabled });

export const useFinopsVercelForecast = () =>
  trpc.finops.vercelForecast.useQuery(undefined, OPTS);

/** Usage detail drawer — `project`/`service` are part of the query key, so it refetches in place. */
export const useFinopsVercelUsage = (
  week: string,
  project?: string,
  service?: string,
  enabled = true,
) => trpc.finops.vercelUsage.useQuery({ week, project, service }, { ...OPTS, enabled });

// ─── History strip (all providers) ───────────────────────────────────────────

export const useFinopsHistory = (provider: FinopsProvider, months = 3) =>
  trpc.finops.history.useQuery({ provider, months }, OPTS);

// ─── Alerts (cloud_costs.aws_health_events) ──────────────────────────────────
// `enabled` lets a caller skip the BigQuery query when the alerts subtab is denied.

/** Open + upcoming, severity-first — the Overview panel. */
export const useFinopsAlertsLatest = (limit = 5, enabled = true) =>
  trpc.finops.alertsLatest.useQuery({ limit }, { ...OPTS, enabled });

/** The Alerts page — filters are the query key; "Load more" is fetchNextPage. */
export const useFinopsAlertsList = (filters: FinopsAlertsFilters, enabled = true) =>
  trpc.finops.alertsList.useInfiniteQuery(filters, {
    ...OPTS,
    enabled,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

/** `healthFeedSince == null` ⇒ no event has ever landed (drives the empty states). */
export const useFinopsAlertsSummary = (enabled = true) =>
  trpc.finops.alertsSummary.useQuery(undefined, { ...OPTS, enabled });

export const useFinopsAlertsFacets = (enabled = true) =>
  trpc.finops.alertsFacets.useQuery(undefined, { ...OPTS, enabled });
